import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { signIn, signOut, getCurrentUser, confirmSignIn, fetchUserAttributes, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';

interface User {
    id: string;
    email: string;
    fullName: string;
    adminRole: string;
}

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    signIn: (email: string, password: string) => Promise<any>;
    confirmSignIn: (newPassword: string) => Promise<void>;
    initiatePasswordReset: (email: string) => Promise<void>;
    confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<any>;
    signOut: () => Promise<void>;
    hasRole: (role: string) => boolean;
    isInPasswordResetFlow: boolean;
    needsPasswordResetCode: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isInPasswordResetFlow, setIsInPasswordResetFlow] = useState(false);
    const [needsPasswordResetCode, setNeedsPasswordResetCode] = useState(false);

    useEffect(() => {
        checkAuthState();
        setupAuthListener();
    }, []);

    const checkAuthState = async () => {
        try {
            console.log('Checking auth state...');
            const currentUser = await getCurrentUser();
            const userAttributes = await fetchUserAttributes();
            
            console.log('Auth state check successful:', { currentUser, userAttributes });
            
            setUser({
                id: currentUser.userId,
                email: userAttributes.email || '',
                fullName: userAttributes['custom:fullName'] || '',
                adminRole: userAttributes['custom:adminRole'] || 'viewer',
            });
        } catch (error) {
            console.log('Auth state check failed (expected in development):', error);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const setupAuthListener = () => {
        Hub.listen('auth', ({ payload }: { payload: { event: string } }) => {
            switch (payload.event) {
                case 'signedIn':
                    checkAuthState();
                    break;
                case 'signedOut':
                    setUser(null);
                    break;
                case 'tokenRefresh':
                    checkAuthState();
                    break;
            }
        });
    };

    const handleSignIn = async (email: string, password: string) => {
        try {
            console.log('AuthContext: Starting sign in...');
            const result = await signIn({ username: email, password });
            console.log('AuthContext: Sign in result:', result);
            
            // If sign in is successful, check auth state immediately
            if (result.isSignedIn) {
                console.log('AuthContext: Sign in successful, checking auth state...');
                await checkAuthState();
                setIsInPasswordResetFlow(false);
                setNeedsPasswordResetCode(false);
            } else if (result.nextStep?.signInStep === 'RESET_PASSWORD') {
                console.log('AuthContext: Setting password reset flow state');
                setIsInPasswordResetFlow(true);
                // For RESET_PASSWORD, we need to determine if it requires a verification code
                // If the user needs to reset password, we should use the forgot password flow
                setNeedsPasswordResetCode(true);
                // Automatically initiate the password reset to send verification code
                await handleInitiatePasswordReset(email);
            }
            
            return result;
        } catch (error: any) {
            console.error('AuthContext: Sign in error:', error);
            
            // Handle PasswordResetRequiredException
            if (error.code === 'PasswordResetRequiredException') {
                console.log('AuthContext: Password reset required, initiating forgot password flow');
                setIsInPasswordResetFlow(true);
                setNeedsPasswordResetCode(true);
                console.log('AuthContext: Set isInPasswordResetFlow=true, needsPasswordResetCode=true');
                // Automatically initiate the password reset
                await handleInitiatePasswordReset(email);
                console.log('AuthContext: Password reset initiated, returning custom result');
                // Return a custom result to indicate password reset is needed
                return {
                    isSignedIn: false,
                    nextStep: {
                        signInStep: 'PASSWORD_RESET_REQUIRED'
                    }
                };
            }
            
            setIsInPasswordResetFlow(false);
            setNeedsPasswordResetCode(false);
            throw error;
        }
    };

    const handleConfirmSignIn = async (newPassword: string) => {
        try {
            console.log('AuthContext: Confirming sign in with new password...');
            await confirmSignIn({ challengeResponse: newPassword });
            console.log('AuthContext: Sign in confirmed successfully');
            await checkAuthState();
            setIsInPasswordResetFlow(false);
            setNeedsPasswordResetCode(false);
        } catch (error) {
            console.error('Confirm sign in error:', error);
            setIsInPasswordResetFlow(false);
            setNeedsPasswordResetCode(false);
            throw error;
        }
    };

    const handleInitiatePasswordReset = async (email: string) => {
        try {
            console.log('AuthContext: Initiating password reset for:', email);
            await resetPassword({ username: email });
            console.log('AuthContext: Password reset initiated successfully');
            setNeedsPasswordResetCode(true);
        } catch (error) {
            console.error('Initiate password reset error:', error);
            throw error;
        }
    };

    const handleConfirmPasswordReset = async (email: string, code: string, newPassword: string) => {
        try {
            console.log('AuthContext: Confirming password reset...');
            await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
            console.log('AuthContext: Password reset confirmed successfully');
            setIsInPasswordResetFlow(false);
            setNeedsPasswordResetCode(false);
            // After successful password reset, automatically sign in the user
            return await handleSignIn(email, newPassword);
        } catch (error) {
            console.error('Confirm password reset error:', error);
            throw error;
        }
    };

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Sign out error:', error);
            throw error;
        }
    };

    const hasRole = (role: string): boolean => {
        if (!user) return false;
        
        // Implement role hierarchy: admin > editor > viewer
        const roleHierarchy = {
            'admin': ['admin', 'editor', 'viewer'],
            'editor': ['editor', 'viewer'],
            'viewer': ['viewer']
        };
        
        const userRole = user.adminRole || 'viewer';
        return roleHierarchy[userRole as keyof typeof roleHierarchy]?.includes(role) || false;
    };

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        signIn: handleSignIn,
        confirmSignIn: handleConfirmSignIn,
        initiatePasswordReset: handleInitiatePasswordReset,
        confirmPasswordReset: handleConfirmPasswordReset,
        signOut: handleSignOut,
        hasRole,
        isInPasswordResetFlow,
        needsPasswordResetCode,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
